import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, output } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { FirebaseNotificationsService } from '@core/services/firebase-notifications.service';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonPrimaryComponent } from '@shared/components/button-primary/button-primary.component';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { NotificationItem } from '@shared/models/notification-item';
import { isTouchDevice } from '@shared/utils/device.utils';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-notifications-menu',
  templateUrl: './notifications-menu.component.html',
  styleUrl: './notifications-menu.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ClickOutsideDirective,
    MatIcon,
    ButtonPrimaryComponent,
  ],
})
export class NotificationsMenuComponent implements OnInit, OnDestroy {
  menuActive = false;
  notifications: NotificationItem[] = [];
  unreadNotificationsCount = 0;
  notificationPermission: NotificationPermission = 'default';
  readonly menuOpened = output<void>();

  private notificationsService = inject(FirebaseNotificationsService);
  private router = inject(Router);
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.notificationsService.initialize();

    this.notificationsService.notifications$.pipe(takeUntil(this.destroy$)).subscribe(notifications => {
      this.notifications = notifications;
    });

    this.notificationsService.unreadCount$.pipe(takeUntil(this.destroy$)).subscribe(count => {
      this.unreadNotificationsCount = count;
    });

    this.notificationsService.permission$.pipe(takeUntil(this.destroy$)).subscribe(permission => {
      this.notificationPermission = permission;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMenu(event: Event, isOpen: boolean): void {
    const touchDevice = isTouchDevice();
    const eventType = event.type;

    if (touchDevice && (eventType === 'mouseenter' || eventType === 'mouseleave')) {
      return;
    }

    switch (eventType) {
      case 'click': {
        event.stopPropagation();
        const nextState = !this.menuActive;
        if (nextState) {
          this.menuOpened.emit();
        }
        this.menuActive = nextState;
        return;
      }
      case 'mouseenter':
        if (isOpen) {
          this.menuOpened.emit();
        }
        this.menuActive = isOpen;
        return;
      case 'mouseleave':
        this.menuActive = false;
        return;
      default:
        return;
    }
  }

  closeMenu(): void {
    this.menuActive = false;
  }

  enableNotifications(): void {
    void this.notificationsService.requestPermission();
  }

  markAllNotificationsRead(): void {
    this.notificationsService.markAllRead();
  }

  removeAllNotifications(event: Event): void {
    event.stopPropagation();
    this.notificationsService.removeAllNotifications();
  }

  handleNotificationClick(notification: NotificationItem): void {
    this.notificationsService.markAsRead(notification.id);
    if (!notification.actionUrl) {
      return;
    }

    if (this.isExternalUrl(notification.actionUrl)) {
      window.open(notification.actionUrl, '_blank');
    } else {
      this.router.navigateByUrl(notification.actionUrl);
    }

    this.closeMenu();
  }

  removeNotification(id: string, event: Event): void {
    event.stopPropagation();
    this.notificationsService.removeNotification(id);
  }

  private isExternalUrl(url: string): boolean {
    return /^https?:\/\//i.test(url);
  }
}
